import { useCallback, useEffect, useState } from "react";
import { Download, FileText, RefreshCw } from "lucide-react";
import client, { extractError } from "@/api";

export const AdminMembershipAgreementPanel = ({ companyId, refreshKey, canRetry, onError }) => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await client.get(`/admin/companies/${companyId}/membership-agreements`);
      setAgreements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      onError?.(extractError(error, "Unable to load membership agreements."));
    } finally {
      setLoading(false);
    }
  }, [companyId, onError]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (agreement) => {
    setBusyId(agreement.id);
    try {
      const response = await client.get(`/admin/membership-agreements/${agreement.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      onError?.(extractError(error, "Unable to download the agreement."));
    } finally {
      setBusyId("");
    }
  };

  const retry = async (agreement) => {
    setBusyId(agreement.id);
    try {
      await client.post(`/admin/membership-agreements/${agreement.id}/retry`);
      await load();
    } catch (error) {
      onError?.(extractError(error, "Agreement generation failed again."));
      await load();
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="credit-panel agreement-panel" data-testid="admin-membership-agreement-panel">
      <div className="section-heading">
        <div><p className="eyebrow">MEMBERSHIP AGREEMENT</p><h3>Business Membership, Barter Credit &amp; Security Cheque</h3></div>
        <FileText size={20} />
      </div>
      {loading && <p className="muted small" data-testid="admin-agreements-loading">Loading agreements…</p>}
      {!loading && agreements.length === 0 && (
        <p className="muted small" data-testid="admin-agreements-empty">No agreement has been issued for this company.</p>
      )}
      <div className="agreement-list">
        {agreements.map((agreement) => (
          <article key={agreement.id} className="agreement-record" data-testid={`admin-agreement-${agreement.id}`}>
            <div className="agreement-record-heading">
              <div>
                <strong data-testid={`admin-agreement-number-${agreement.id}`}>{agreement.agreement_number}</strong>
                <small data-testid={`admin-agreement-plan-${agreement.id}`}>{agreement.membership_plan_name}</small>
              </div>
              <span className={`chip ${agreement.status === "GENERATED" ? "chip-success" : "chip-warning"}`} data-testid={`admin-agreement-status-${agreement.id}`}>{agreement.status}</span>
            </div>
            <dl className="agreement-details">
              <div><dt>Allocated Barter/Trade Credit</dt><dd data-testid={`admin-agreement-credit-${agreement.id}`}>₹{Number(agreement.allocated_barter_credit_limit).toLocaleString("en-IN")}</dd></div>
              <div><dt>Security Cheque Amount</dt><dd data-testid={`admin-agreement-cheque-amount-${agreement.id}`}>₹{Number(agreement.security_cheque_amount).toLocaleString("en-IN")}</dd></div>
              <div><dt>Cheque Number</dt><dd data-testid={`admin-agreement-cheque-number-${agreement.id}`}>{agreement.security_cheque_number}</dd></div>
              <div><dt>Bank Name</dt><dd data-testid={`admin-agreement-bank-${agreement.id}`}>{agreement.security_cheque_bank_name}</dd></div>
              <div><dt>Cheque Date</dt><dd data-testid={`admin-agreement-cheque-date-${agreement.id}`}>BLANK</dd></div>
              <div><dt>Generated At</dt><dd data-testid={`admin-agreement-generated-at-${agreement.id}`}>{agreement.generated_at ? new Date(agreement.generated_at).toLocaleString() : "—"}</dd></div>
            </dl>
            {agreement.generation_error && <p className="form-error small" data-testid={`admin-agreement-error-${agreement.id}`}>{agreement.generation_error}</p>}
            <div className="detail-actions">
              {agreement.status === "GENERATED" && (
                <button type="button" className="ghost-button" onClick={() => download(agreement)} disabled={busyId === agreement.id} data-testid={`admin-agreement-download-${agreement.id}`}>
                  <Download size={15} /> Download
                </button>
              )}
              {canRetry && agreement.status !== "GENERATED" && (
                <button type="button" className="ghost-button" onClick={() => retry(agreement)} disabled={busyId === agreement.id} data-testid={`admin-agreement-retry-${agreement.id}`}>
                  <RefreshCw size={15} /> {busyId === agreement.id ? "Retrying…" : "Retry"}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};